<template>
  <form @submit.prevent="handleSubmit">
    <div :class="formPaddingClasses.field">
      <UIFormField
        v-for="field in fields"
        :key="field.name"
        v-model="formData[field.name]"
        :field="field"
        :type="field.type"
        :label="field.label"
        :required="field.required"
        :hint="field.hint"
        :helper-text="field.helperText"
        :placeholder="field.placeholder"
        :options="field.options"
        :error="errors[field.name]"
        @change="(e: Event) => handleFieldChange(field.name, (e.target as HTMLInputElement).value)"
      />

      <!-- Slot for custom form elements -->
      <slot name="fields" />

      <!-- Actions slot with loading state from store -->
      <slot name="actions" :loading="loadingStore.isLoading">
        <UIButton v-if="submitLabel"
          type="submit"
          variant="primary"
          :loading="loadingStore.isLoading"
          full-width
        >
          {{ submitLabel }}
        </UIButton>
      </slot>
    </div>
  </form>
</template>

<script setup lang="ts">
import { logger, createLogger } from '@/utils/logger'

// Create a namespaced logger
const fileLogger = createLogger('BaseForm')

import { ref, reactive } from 'vue'
import { useLoadingStore } from '@/stores/loading'
import { getConfig, getFormPaddingClasses } from '@/config'
import UIFormField from '@/components/ui/UIFormField.vue'
import UIButton from '@/components/ui/UIButton.vue'
import { validateForm } from '@/utils/validation'
import type { IFormField } from '@/types/form'

const props = defineProps<{
  fields: IFormField[]
  initialData?: Record<string, any>
  submitLabel?: string
  autoSubmit?: boolean
}>()

const emit = defineEmits<{
  submit: [formData: Record<string, any>]
  fieldChange: [field: { name: string; value: any }]
}>()

const config = getConfig()
const formPaddingClasses = getFormPaddingClasses(config)
const loadingStore = useLoadingStore()
const errors = reactive<Record<string, string>>({})
const formData = reactive({ ...props.initialData })

const validateFields = async () => {
  const validations = props.fields.reduce(
    (acc, field) => {
      if (field.required) {
        acc[field.name] = [
          (value: any) => {
            return value ? true : `${field.label} is required`
          },
        ]
      }
      return acc
    },
    {} as Record<string, ((value: any) => string | true)[]>
  )

  return validateForm(formData, validations)
}

const handleSubmit = async () => {
  if (loadingStore.isLoading) return // Prevent multiple submissions

  loadingStore.startLoading()
  Object.keys(errors).forEach((key) => delete errors[key])

  try {
    // First validate
    const validationErrors = await validateFields()
    if (Object.keys(validationErrors).length > 0) {
      Object.assign(errors, validationErrors)
      loadingStore.stopLoading()
      return
    }

    // Then submit and wait for it to complete (support async submit handlers)
    const results = emit('submit', formData)
    const result = Array.isArray(results) ? results[0] : undefined
    if (result != null && typeof (result as Promise<unknown>)?.then === 'function') {
      await (result as Promise<unknown>)
    }
  } catch (error) {
    if (error instanceof Error) {
      fileLogger.error('Form submission error:', error.message)
    }
  } finally {
    loadingStore.stopLoading()
  }
}

const setErrors = (newErrors: Record<string, string>) => {
  Object.assign(errors, newErrors)
}

const handleFieldChange = (name: string, value: any) => {
  emit('fieldChange', { name, value })
}

defineExpose({
  setErrors,
})
</script>
